import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  Edit3,
} from 'lucide-react';
import workOrderService from '../../services/productionService';
import warehouseService from '../../services/warehouseService';
import InboundItemsTable from '../InboundItemsTable';

const CompleteWorkOrderDialog = ({
  open,
  onClose,
  workOrder,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [linkedMovements, setLinkedMovements] = useState([]);

  const [actualMaterialsUsed, setActualMaterialsUsed] = useState(null);
  const [defaultLocation, setDefaultLocation] = useState('main');
  
  const [productCompletions, setProductCompletions] = useState([]);
  
  const [remainingBars, setRemainingBars] = useState([]);
  const [initialCalculatedBars, setInitialCalculatedBars] = useState([]);
  const [leftoverOverrideMode, setLeftoverOverrideMode] = useState(false);
  const [leftoverJustification, setLeftoverJustification] = useState('');
  const [hasInitializedLeftovers, setHasInitializedLeftovers] = useState(false);
  
  const [showAddRemaining, setShowAddRemaining] = useState(false);
  const [newRemainingBar, setNewRemainingBar] = useState({
    material_code: '',
    material_name: '',
    length: '',
    quantity: 1,
    location: 'main',
    product_id: '',
    leftover_type: 'blank',
    tip_diameter: ''
  });

  const availableMaterials = useMemo(() => {
    if (linkedMovements && linkedMovements.length > 0) {
      const materials = new Map();

      linkedMovements.forEach(move => {
        if (move.products && Array.isArray(move.products)) {
          move.products.forEach(prod => {
            const code = prod.product_id || prod.material_product_code || prod.product_code;
            const name = prod.product_name || prod.material_product_name || '';
            const id = prod._id || prod.material_product_id || prod.product_id;

            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name,
                id: id || '',
                product_code: prod.finished_product_code || ''
              });
            }
          });
        }
      });

      if (materials.size > 0) {
        return Array.from(materials.values());
      }
    }

    if (workOrder?.items) {
      const materials = new Map();
      workOrder.items.forEach(item => {
        if (item.material_requirements && Array.isArray(item.material_requirements)) {
          item.material_requirements.forEach(mat => {
            const code = mat.material_product_code || mat.material_code;
            const id = mat.material_product_id || mat._id;
            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name: mat.material_name || mat.material_product_name || '',
                id: id || '',
                product_code: item.finished_product_code || mat.product_code || ''
              });
            }
          });
        }
      });
      return Array.from(materials.values());
    }

    return [];
  }, [linkedMovements, workOrder]);

  const totalCompleted = useMemo(() => {
    if (workOrder?.items && workOrder.items.length > 0) {
      let completed = 0;
      workOrder.items.forEach(item => {
        if (item.process_steps && item.process_steps.length > 0) {
          const steps = item.process_steps;
          const lastStep = steps.reduce((max, s) => s.sequence > max.sequence ? s : max, { sequence: -1, quantity_completed: 0 });
          completed += Number(lastStep.quantity_completed || 0);
        }
      });
      return completed;
    }

    const history = workOrder?.progress_history || [];
    const completed = history.reduce((sum, entry) => sum + (entry.completed_quantity || 0), 0);
    return completed || Number(workOrder?.completed_quantity || 0);
  }, [workOrder]);

  const calculateAutoLeftovers = useCallback((order, movements, completions) => {
    if (!order?.items || !movements?.length) return [];
    
    const completionByItemIndex = new Map();
    completions.forEach(c => {
      completionByItemIndex.set(c.item_index, parseFloat(c.quantity) || 0);
    });
    
    // Group by product_id (material), collect all usages (items using this material)
    const materialGroups = new Map();
    
    order.items.forEach((item, idx) => {
      const mi = item.material_info;
      if (!mi?.bar_length || !mi.pieces_per_bar) return;
      
      const completionQty = completionByItemIndex.get(idx) || 0;
      if (completionQty <= 0) return;
      
      const pid = mi.product_id;
      if (!pid) return;
      
      if (!materialGroups.has(pid)) {
        materialGroups.set(pid, {
          usages: [],
          barLength: parseFloat(mi.bar_length),
          productId: pid,
          productCode: mi.product_code,
          productName: mi.product_name,
          tipDiameter: mi.tip_diameter
        });
      }
      
      materialGroups.get(pid).usages.push({
        completionQty,
        productLength: parseFloat(mi.product_length) || 0,
        piecesPerBar: parseInt(mi.pieces_per_bar) || 1
      });
    });
    
    // Aggregate issued quantities by product_id (_id)
    const issuedByProductId = new Map();
    movements.forEach(move => {
      move.products?.forEach(prod => {
        const qty = parseFloat(prod.quantity) || 0;
        const pid = prod._id || prod.material_product_id;
        if (pid) {
          issuedByProductId.set(String(pid), (issuedByProductId.get(String(pid)) || 0) + qty);
        }
      });
    });
    
    const MIN_REMAINING_LENGTH = 30;
    const leftovers = [];
    
    materialGroups.forEach((group) => {
      const actualIssued = issuedByProductId.get(String(group.productId)) || 0;
      const { barLength, productId, productCode, productName, tipDiameter } = group;
      
      // Total theoretical bars needed across all items using this material
      const totalTheoryBars = group.usages.reduce((sum, u) => sum + Math.ceil(u.completionQty / u.piecesPerBar), 0);
      
      if (totalTheoryBars <= 0 || actualIssued <= 0) return;
      
      const fullRemainingBars = Math.max(0, actualIssued - totalTheoryBars);
      const displayCode = productCode || productId || 'Unknown';
      
      // Excess fully-issued bars
      if (fullRemainingBars > 0) {
        leftovers.push({
          material_code: displayCode,
          material_name: productName,
          length: barLength,
          quantity: fullRemainingBars,
          location: 'main',
          product_id: productId,
          tip_diameter: tipDiameter,
          is_calculated: true,
          calculation_source: 'excess_issue',
          leftover_type: 'blank'
        });
      }
      
      // Cutting leftovers: allocate bars to each usage sequentially
      let barsAvailable = actualIssued - fullRemainingBars;
      
      group.usages.forEach(usage => {
        const usageTheoryBars = Math.ceil(usage.completionQty / usage.piecesPerBar);
        const allocatedBars = Math.min(barsAvailable, usageTheoryBars);
        barsAvailable -= allocatedBars;
        
        if (allocatedBars <= 0 || usage.productLength <= 0) return;
        
        const { productLength, piecesPerBar } = usage;
        const productsOnLastBar = usage.completionQty - (allocatedBars - 1) * piecesPerBar;
        const isLastBarFull = productsOnLastBar >= piecesPerBar;
        
        if (isLastBarFull) {
          const remainder = barLength - piecesPerBar * productLength;
          if (remainder >= MIN_REMAINING_LENGTH) {
            leftovers.push({
              material_code: displayCode,
              material_name: productName,
              length: Math.round(remainder * 100) / 100,
              quantity: allocatedBars,
              location: 'main',
              product_id: productId,
              tip_diameter: tipDiameter,
              is_calculated: true,
              calculation_source: 'cut_remaining',
              leftover_type: 'blank'
            });
          }
        } else {
          const fullBarRemainder = barLength - piecesPerBar * productLength;
          if (allocatedBars > 1 && fullBarRemainder >= MIN_REMAINING_LENGTH) {
            leftovers.push({
              material_code: displayCode,
              material_name: productName,
              length: Math.round(fullBarRemainder * 100) / 100,
              quantity: allocatedBars - 1,
              location: 'main',
              product_id: productId,
              tip_diameter: tipDiameter,
              is_calculated: true,
              calculation_source: 'cut_remaining',
              leftover_type: 'blank'
            });
          }
          
          const lastBarRemainder = barLength - productsOnLastBar * productLength;
          if (lastBarRemainder >= MIN_REMAINING_LENGTH) {
            leftovers.push({
              material_code: displayCode,
              material_name: productName,
              length: Math.round(lastBarRemainder * 100) / 100,
              quantity: 1,
              location: 'main',
              product_id: productId,
              tip_diameter: tipDiameter,
              is_calculated: true,
              calculation_source: 'cut_remaining',
              leftover_type: 'blank'
            });
          }
        }
      });
    });
    
    return leftovers;
  }, []);

  const loadData = useCallback(async () => {
    if (!workOrder) return;
    setLoading(true);
    setError(null);
    try {
      if (workOrder.items && workOrder.items.length > 0) {
        const initialCompletions = workOrder.items.map((item, idx) => ({
          id: idx,
          item_index: idx,
          product_code: item.finished_product_code,
          product_name: item.finished_product_name,
          specification: item.product_details?.specification || item.specification || '',
          planned_quantity: item.planned_quantity || 0,
          completed_quantity: item.completed_quantity || 0,
          quantity: Math.max(0, (item.planned_quantity || 0) - (item.completed_quantity || 0)),
          location: 'main',
          use_custom_location: false
        }));
        setProductCompletions(initialCompletions);
      }

      try {
        let movementData = workOrder.material_issue_movements || [];
        
        // Backend _serialize_order already replaces IDs with full movement objects
        const isAlreadyFullObjects = Array.isArray(movementData) && movementData.length > 0 && 
          typeof movementData[0] === 'object' && movementData[0].movement_id;
        
        if (isAlreadyFullObjects) {
          setLinkedMovements(movementData);
        } else if (Array.isArray(movementData) && movementData.length > 0) {
          const movements = await warehouseService.getMovementsByIds(movementData);
          setLinkedMovements(movements);
        } else {
          setLinkedMovements([]);
        }
      } catch (err) {
        console.error('Failed to load movements:', err);
        setLinkedMovements([]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  useEffect(() => {
    if (open && workOrder) {
      loadData();
      setRemainingBars([]);
      setInitialCalculatedBars([]);
      setLeftoverOverrideMode(false);
      setLeftoverJustification('');
      setHasInitializedLeftovers(false);
      setDefaultLocation('main');
      setActualMaterialsUsed(null);
    }
  }, [open, workOrder, loadData]);

  // Auto-calculate leftovers when movements and completions are loaded
  useEffect(() => {
    if (!workOrder || !open || !hasInitializedLeftovers || initialCalculatedBars.length > 0) return;
    if (linkedMovements.length > 0 && productCompletions.length > 0 && remainingBars.length === 0) {
      const autoLeftovers = calculateAutoLeftovers(workOrder, linkedMovements, productCompletions);
      if (autoLeftovers.length > 0) {
        const barsWithId = autoLeftovers.map((bar, idx) => ({
          ...bar,
          id: `auto-${Date.now()}-${idx}`
        }));
        setRemainingBars(barsWithId);
        setInitialCalculatedBars(barsWithId.map(b => ({ ...b })));
      }
    }
  }, [workOrder, linkedMovements, productCompletions, hasInitializedLeftovers, open, calculateAutoLeftovers, remainingBars.length, initialCalculatedBars.length]);

  // Track when data is ready for auto-calculation
  useEffect(() => {
    if (!loading && linkedMovements.length >= 0 && productCompletions.length > 0) {
      setHasInitializedLeftovers(true);
    }
  }, [loading, linkedMovements.length, productCompletions.length]);

  // Detect override mode
  useEffect(() => {
    if (initialCalculatedBars.length === 0 || leftoverOverrideMode) return;
    
    const autoIds = new Set(initialCalculatedBars.map(b => b.id));
    const hasNewBars = remainingBars.some(b => !autoIds.has(b.id));
    const currentIds = new Set(remainingBars.map(b => b.id));
    const hasDeletedBars = initialCalculatedBars.some(b => !currentIds.has(b.id));
    const hasModifiedBars = remainingBars.some(bar => {
      const original = initialCalculatedBars.find(b => b.id === bar.id);
      if (!original) return false;
      return Math.abs((parseFloat(bar.length) || 0) - (parseFloat(original.length) || 0)) > 0.01 ||
             Math.abs((parseInt(bar.quantity) || 0) - (parseInt(original.quantity) || 0)) > 0;
    });
    
    if (hasNewBars || hasDeletedBars || hasModifiedBars) {
      setLeftoverOverrideMode(true);
    }
  }, [remainingBars, initialCalculatedBars, leftoverOverrideMode]);

  // If no auto-calculation but user adds bars, also enter override mode
  useEffect(() => {
    if (initialCalculatedBars.length === 0 && remainingBars.length > 0 && !leftoverOverrideMode) {
      setLeftoverOverrideMode(true);
    }
  }, [remainingBars.length, initialCalculatedBars.length, leftoverOverrideMode]);

  const updateProductQuantity = (itemIndex, quantity) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { ...p, quantity: parseFloat(quantity) || 0 } : p)
    );
  };

  const updateProductLocation = (itemIndex, location) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { ...p, location } : p)
    );
  };

  const toggleCustomLocation = (itemIndex, useCustom) => {
    setProductCompletions(prev => 
      prev.map(p => p.item_index === itemIndex ? { 
        ...p, 
        use_custom_location: useCustom,
        location: useCustom ? p.location : defaultLocation
      } : p)
    );
  };

  const handleDefaultLocationChange = (newDefault) => {
    setDefaultLocation(newDefault);
    setProductCompletions(prev =>
      prev.map(p => p.use_custom_location ? p : { ...p, location: newDefault })
    );
  };

  const handleProductCompletionChange = (id, field, value) => {
    if (field === 'quantity') {
      updateProductQuantity(id, value);
    } else if (field === 'location') {
      updateProductLocation(id, value);
    } else if (field === 'use_custom_location') {
      toggleCustomLocation(id, value);
    }
  };

  const updateRemainingBar = (id, field, value) => {
    setRemainingBars(prev => prev.map(bar => {
      if (bar.id !== id) return bar;
      if (field === 'length') return { ...bar, length: parseFloat(value) || 0 };
      if (field === 'quantity') return { ...bar, quantity: parseInt(value) || 0 };
      if (field === 'location') return { ...bar, location: value };
      if (field === 'leftover_type') return { ...bar, leftover_type: value };
      if (field === 'tip_diameter') return { ...bar, tip_diameter: parseFloat(value) || 0 };
      return bar;
    }));
  };

  const handleAddRemainingBar = () => {
    if (!newRemainingBar.material_code) {
      setError('请选择物料类型');
      return;
    }
    if (!newRemainingBar.length || parseFloat(newRemainingBar.length) <= 0) {
      setError('请输入有效的余料长度');
      return;
    }

    const selectedMaterial = availableMaterials.find(
      m => m.code === newRemainingBar.material_code
    );

    setRemainingBars([...remainingBars, {
      ...newRemainingBar,
      id: `manual-${Date.now()}`,
      material_code: newRemainingBar.material_code,
      material_name: selectedMaterial?.name || '',
      product_code: selectedMaterial?.product_code || '',
      product_id: selectedMaterial?.id || '',
      length: parseFloat(newRemainingBar.length),
      quantity: parseInt(newRemainingBar.quantity) || 1,
      is_calculated: false,
      leftover_type: newRemainingBar.leftover_type || 'blank',
      tip_diameter: newRemainingBar.leftover_type === 'ground' 
        ? (parseFloat(newRemainingBar.tip_diameter) || 0)
        : (selectedMaterial?.tip_diameter || 0)
    }]);

    setNewRemainingBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: '',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setShowAddRemaining(false);
    setError(null);
  };

  const removeRemainingBar = (id) => {
    setRemainingBars(remainingBars.filter(bar => bar.id !== id));
  };

  const handleRestoreCalculated = () => {
    setRemainingBars(initialCalculatedBars.map(b => ({ ...b })));
    setLeftoverOverrideMode(false);
    setLeftoverJustification('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      if (leftoverOverrideMode && !leftoverJustification.trim()) {
        setError('请填写余料修改理由');
        setSubmitting(false);
        return;
      }

      const calculatedTotal = productCompletions.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);
      
      const productQuantities = productCompletions
        .filter(p => p.item_index !== undefined && p.item_index !== null)
        .map(p => ({
          item_index: p.item_index,
          quantity: parseFloat(p.quantity) || 0,
          location: p.use_custom_location ? p.location : defaultLocation,
          specification: p.specification || ''
        }));

      const result = await workOrderService.completeProduction(
        workOrder._id,
        calculatedTotal,
        0,
        '',
        actualMaterialsUsed ? parseFloat(actualMaterialsUsed) : null,
        remainingBars,
        productQuantities,
        defaultLocation,
        leftoverOverrideMode ? leftoverJustification : ''
      );
      
      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Failed to complete work order:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '完成工单失败';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setRemainingBars([]);
    setInitialCalculatedBars([]);
    setLeftoverOverrideMode(false);
    setLeftoverJustification('');
    setHasInitializedLeftovers(false);
    setShowAddRemaining(false);
    onClose();
  };

  const plannedQty = workOrder?.total_planned_quantity || workOrder?.planned_quantity || 0;
  const existingCompleted = (workOrder?.items || []).reduce(
    (sum, item) => sum + (parseFloat(item.completed_quantity) || 0),
    0
  );
  const calculatedTotal = productCompletions.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>完成工单</DialogTitle>
          <DialogDescription>
            确认工单 {workOrder?.production_order_id} 的完成信息
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground">工序完成数量</Label>
                <div className="text-lg font-semibold">{totalCompleted}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">计划数量</Label>
                <div className="text-lg font-semibold">{plannedQty}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">已入库数量</Label>
                <div className="text-lg font-semibold">{existingCompleted}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">本次入库总数</Label>
                <div className={`text-lg font-semibold ${calculatedTotal !== totalCompleted ? 'text-amber-600' : ''}`}>
                  {calculatedTotal}
                  {calculatedTotal !== totalCompleted && (
                    <span className="text-xs ml-2 font-normal">(与工序完成数不一致)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Product Completion Table */}
            <Card>
              <CardContent className="pt-6">
                <InboundItemsTable
                  items={productCompletions}
                  defaultLocation={defaultLocation}
                  onDefaultLocationChange={handleDefaultLocationChange}
                  onItemChange={handleProductCompletionChange}
                  disabled={submitting}
                  showReceived
                  receivedLabel="已入库"
                  quantityLabel="入库数量"
                  title="成品入库明细"
                />
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>实际使用物料数量</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={actualMaterialsUsed || ''}
                    onChange={(e) => setActualMaterialsUsed(e.target.value)}
                    placeholder="手动输入实际消耗（可选）"
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    如不填写则不记录实际消耗
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-base">
                      {initialCalculatedBars.length > 0 && !leftoverOverrideMode 
                        ? '系统计算余料' 
                        : '余料录入'}
                    </Label>
                    {leftoverOverrideMode && (
                      <Badge variant="warning">已修改</Badge>
                    )}
                    {initialCalculatedBars.length > 0 && !leftoverOverrideMode && (
                      <Badge variant="secondary">自动</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {initialCalculatedBars.length > 0 && leftoverOverrideMode && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleRestoreCalculated}
                        disabled={submitting}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        恢复计算值
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddRemaining(true)}
                      disabled={submitting || showAddRemaining}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      添加余料
                    </Button>
                  </div>
                </div>

                {showAddRemaining && (
                  <Card className="p-3 border-dashed">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">选择物料</Label>
                        <Select
                          value={newRemainingBar.material_code}
                          onValueChange={(value) => {
                            const material = availableMaterials.find(m => m.code === value);
                            setNewRemainingBar({
                              ...newRemainingBar,
                              material_code: value,
                              material_name: material?.name || '',
                              product_code: material?.product_code || ''
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="选择物料类型" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMaterials.map((material) => (
                              <SelectItem key={material.code} value={material.code}>
                                {material.code} - {material.name} ({material.product_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">余料类型</Label>
                        <Select
                          value={newRemainingBar.leftover_type}
                          onValueChange={(value) => {
                            setNewRemainingBar({
                              ...newRemainingBar,
                              leftover_type: value,
                              tip_diameter: value === 'blank' ? '' : newRemainingBar.tip_diameter
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="选择余料类型" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="blank">毛坯余料</SelectItem>
                            <SelectItem value="ground">精磨余料</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">长度(mm)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.length}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, length: e.target.value})}
                            placeholder="长度"
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">数量(根)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.quantity}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, quantity: e.target.value})}
                            placeholder="数量"
                            className="h-8"
                          />
                        </div>
                      </div>
                      
                      {newRemainingBar.leftover_type === 'ground' && (
                        <div className="space-y-1">
                          <Label className="text-xs">刃径(mm)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newRemainingBar.tip_diameter}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, tip_diameter: e.target.value})}
                            placeholder="精磨后刃径"
                            className="h-8"
                          />
                          <p className="text-[10px] text-muted-foreground">精磨余料需手动输入实际刃径</p>
                        </div>
                      )}
                      
                      <div className="space-y-1">
                        <Label className="text-xs">位置</Label>
                        <Input
                          value={newRemainingBar.location}
                          onChange={(e) => setNewRemainingBar({...newRemainingBar, location: e.target.value})}
                          placeholder="位置"
                          className="h-8"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setShowAddRemaining(false)}>取消</Button>
                        <Button size="sm" onClick={handleAddRemainingBar}>确认添加</Button>
                      </div>
                    </div>
                  </Card>
                )}

                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>物料</TableHead>
                        <TableHead>类型</TableHead>
                        <TableHead>长度</TableHead>
                        <TableHead>数量</TableHead>
                        <TableHead>库位</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {remainingBars.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-4">
                            无余料记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        remainingBars.map((bar) => (
                          <TableRow key={bar.id}>
                            <TableCell className="font-medium">
                              <div className="flex flex-col">
                                <span>{bar.material_code}</span>
                                {bar.is_calculated && !leftoverOverrideMode && (
                                  <span className="text-xs text-muted-foreground">系统计算</span>
                                )}
                                {bar.leftover_type === 'ground' && bar.tip_diameter > 0 && (
                                  <span className="text-xs text-blue-600">刃径 {bar.tip_diameter}mm</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {leftoverOverrideMode || bar.is_calculated === false ? (
                                <Select
                                  value={bar.leftover_type || 'blank'}
                                  onValueChange={(value) => updateRemainingBar(bar.id, 'leftover_type', value)}
                                >
                                  <SelectTrigger className="h-8 w-24">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="blank">毛坯</SelectItem>
                                    <SelectItem value="ground">精磨</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant={bar.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-xs">
                                  {bar.leftover_type === 'ground' ? '精磨' : '毛坯'}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {leftoverOverrideMode ? (
                                <Input
                                  type="number"
                                  value={bar.length}
                                  onChange={(e) => updateRemainingBar(bar.id, 'length', e.target.value)}
                                  className="w-20 h-8"
                                  disabled={submitting}
                                />
                              ) : (
                                `${bar.length}mm`
                              )}
                            </TableCell>
                            <TableCell>
                              {leftoverOverrideMode ? (
                                <Input
                                  type="number"
                                  value={bar.quantity}
                                  onChange={(e) => updateRemainingBar(bar.id, 'quantity', e.target.value)}
                                  className="w-16 h-8"
                                  disabled={submitting}
                                />
                              ) : (
                                bar.quantity
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                value={bar.location}
                                onChange={(e) => updateRemainingBar(bar.id, 'location', e.target.value)}
                                className="w-28 h-8"
                                placeholder="库位"
                                disabled={submitting}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeRemainingBar(bar.id)}
                                disabled={submitting}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {leftoverOverrideMode && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-amber-600">
                      修改理由 <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      value={leftoverJustification}
                      onChange={(e) => setLeftoverJustification(e.target.value)}
                      placeholder="请说明余料与系统计算值不一致的原因（必填）"
                      className="min-h-[80px]"
                      disabled={submitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      系统已根据BOM和领料记录自动计算理论余料。如实际余料与计算不符，请说明原因以便成本分析。
                    </p>
                  </div>
                )}

                {initialCalculatedBars.length > 0 && !leftoverOverrideMode && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLeftoverOverrideMode(true)}
                      disabled={submitting}
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      修改计算值
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                取消
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || (calculatedTotal === 0 && existingCompleted < plannedQty)}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                完成工单
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompleteWorkOrderDialog;
